import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { trackEventOnClick } from "@faire/web/common/trackEvent";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";

import {
  Carousel,
  CarouselHorizontalScrollCarouselItem,
  IProps as ICarouselProps,
} from "@faire/retailer/components/Carousel/Carousel";
import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { ProductTile } from "@faire/retailer/features/MarketplaceItems/ProductTile";
import { brandPage } from "@faire/retailer/paths";
import { generateProductUrlForProductPage } from "@faire/retailer/views/product/utils/generateProductUrlForProductPage";

interface IProps {
  loading: boolean;
  brandToken: string;
  productTiles: ILeanProductTile[];
  totalProductsCount: number;
  trackProductClick: (token: string) => void;
  hideMoreProductsLink?: boolean;
}

const CarouselContainer = styled.div``;

const StyledCarousel = styled(
  Carousel as React.ComponentType<ICarouselProps<ILeanProductTile>>
)`
  width: calc(100% + 40px);
  margin: 0 -20px;
  ${"" /* This hides the horizontal scrollbar on mobile */};
  overflow-y: hidden;

  ${responsiveMediaQueries.tabletAndAbove} {
    overflow-y: unset;
    width: 100%;
    margin: 0;
  }

  ${CarouselHorizontalScrollCarouselItem} {
    padding: 0 4px;

    &:last-child {
      padding: 0 20px 0 4px;
    }
  }
`;

export const BrandCatalog: React.FC<IProps> = ({
  loading,
  brandToken,
  productTiles,
  totalProductsCount,
  hideMoreProductsLink,
  trackProductClick,
}) => {
  const { isMobile } = useViewport();

  const itemsPerSlide = useResponsiveValue({
    mobileAndAbove: 2,
    tabletAndAbove: 3,
    desktopAndAbove: 5,
  });

  const renderItem = (productTile: ILeanProductTile) => {
    return (
      <ProductTile
        key={productTile?.product?.token}
        productTile={productTile}
        onProductClick={trackProductClick}
        generateProductLinkProps={(productToken: string) => ({
          productUrl: generateProductUrlForProductPage(productToken),
        })}
        imageDimension={{
          mobileAndAbove: 80,
          tabletAndAbove: 250,
        }}
        newTab
      />
    );
  };

  if (loading) {
    return (
      <SpinnerContainer>
        <LoadingSpinner />
      </SpinnerContainer>
    );
  }

  return (
    <Column align="center">
      <CarouselContainer>
        <StyledCarousel
          padItems
          name={`brand-catalog/${brandToken}/product-list`}
          peekNext={isMobile}
          items={productTiles}
          itemGap={20}
          itemsPerSlide={itemsPerSlide}
          renderItem={renderItem}
          buttonsOffsetTop="calc(50% - 50px)"
        />
      </CarouselContainer>
      {productTiles.length !== totalProductsCount && !hideMoreProductsLink ? (
        <>
          <Spacer size="4x" />
          <ViewAllLink
            href={brandPage(brandToken)}
            target="_blank"
            rel="noreferrer"
            onClick={trackEventOnClick("brand-catalog/see-all-products", true)}
          >
            <Button variant="primary">
              <StrictLocalMsg
                defaultMessage="See All {num} Products"
                values={DEPRECATED_backwardsCompatibleValues({
                  num: totalProductsCount,
                })}
              />
            </Button>
          </ViewAllLink>
        </>
      ) : null}
    </Column>
  );
};

const SpinnerContainer = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  height: 320px;
`;

const ViewAllLink = styled(Anchor)`
  button {
    padding: 0 32px;
    width: auto;
  }
`;
